import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Labor/LogPaymentModal.css';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../../../../../../firebase';
import { useDispatch } from 'react-redux';
import {
  logWorkerPayment,
  getWorkerPayments,
  refreshLaborProject, // ✅ use if you want metrics/outstanding refresh
} from '../../../../../../../Redux/Projects/Labor/laborSlice';
import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';
import { useParams } from 'react-router-dom';

const LogPaymentModal = ({ worker, onClose }) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const [date, setDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [includeInExpense, setIncludeInExpense] = useState(true);
  const [loading, setLoading] = useState(false);

  const amountError = useMemo(() => {
    const n = Number(amount);
    if (!amount) return 'Amount is required';
    if (!Number.isFinite(n) || n <= 0) return 'Amount must be greater than 0';
    if (n > 100000000) return 'Amount looks too large';
    return '';
  }, [amount]);

  const onReceiptPick = (e) => {
    const f = e.target.files?.[0] || null;
    setReceipt(f);
    e.target.value = ''; // allow reselect same file
  };

  const handleSubmit = async () => {
    if (!projectId) {
      dispatch(showFeedback({ type: 'error', title: 'No project', message: 'Project id missing.' }));
      return;
    }

    if (!worker?._id) {
      dispatch(showFeedback({ type: 'error', title: 'Worker missing', message: 'Worker not found.' }));
      return;
    }

    if (amountError) {
      dispatch(showFeedback({ type: 'error', title: 'Fix required fields', message: amountError }));
      return;
    }

    // date validation
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
      dispatch(showFeedback({ type: 'error', title: 'Invalid date', message: 'Please select a valid date.' }));
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      let receiptUrl = '';
      let receiptName = '';

      if (receipt) {
        const ext = (receipt.name.split('.').pop() || 'bin')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '') || 'bin';

        const storagePath = `receipts/${projectId}/${worker._id}/${Date.now()}.${ext}`;
        const storageRef = ref(storage, storagePath);

        await uploadBytes(storageRef, receipt, {
          contentType: receipt.type || 'application/octet-stream',
        });

        receiptUrl = await getDownloadURL(storageRef);
        receiptName = receipt.name;
      }

      const action = await dispatch(
        logWorkerPayment({
          workerId: worker._id,
          data: {
            projectId,
            date: date.toISOString(),
            amount: Number(amount),
            note: note?.trim() || '',
            mode: 'cash', // must be valid enum
            receiptName,
            receiptUrl,
            createExpense: includeInExpense,
          },
        })
      );

      if (logWorkerPayment.rejected?.match?.(action)) {
        const msg = action.payload?.message || action.error?.message || 'Failed to log payment.';
        dispatch(showFeedback({ type: 'error', title: 'Failed', message: msg }));
        return;
      }

      // refresh drawer payments
      dispatch(getWorkerPayments(worker._id));

      // ✅ optional: refresh summary/outstanding/activity for project
      dispatch(refreshLaborProject(projectId));

      const expense = action.payload?.expense;

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Payment logged',
          message: expense
            ? `₹${Number(amount).toLocaleString('en-IN')} logged and added to Expenses.`
            : `₹${Number(amount).toLocaleString('en-IN')} logged successfully.`,
        })
      );

      onClose();
    } catch (err) {
      console.error('Error logging payment:', err);
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Error',
          message: err?.message || 'Something went wrong while logging payment.',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="log-payment-modal-overlay" onMouseDown={onClose}>
      <div className="log-payment-modal-container" onMouseDown={(e) => e.stopPropagation()}>
        <div className="log-payment-modal-header">
          <h3>Log Payment</h3>
          <button className="log-payment-modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="log-payment-modal-worker-info">
          <img src={worker?.avatar || 'https://i.ibb.co/j3pW4yv/user.png'} alt="avatar" />
          <span>{worker?.firstName} {worker?.lastName}</span>
        </div>

        <div className="log-payment-modal-form-group">
          <label>Date</label>
          <DatePicker
            selected={date}
            onChange={(d) => setDate(d || new Date())}
            className="log-payment-modal-form-control"
            dateFormat="dd MMMM yyyy"
            disabled={loading}
          />
        </div>

        <div className="log-payment-modal-form-group">
          <label>Amount</label>
          <input
            type="number"
            placeholder="₹ Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="log-payment-modal-form-control"
            disabled={loading}
          />
          {amountError && <div className="input-error">{amountError}</div>}
        </div>

        <div className="log-payment-modal-form-group">
          <label>Notes (Optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="log-payment-modal-form-control"
            rows="3"
            disabled={loading}
          />
        </div>

        <div className="log-payment-modal-form-group">
          <label>Upload Receipt</label>
          <div className="log-payment-upload-wrapper">
            <label
              htmlFor="upload-receipt"
              className="log-payment-upload-btn"
              style={loading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
            >
              <span className="upload-icon">↑</span> Upload file
            </label>

            <input
              id="upload-receipt"
              type="file"
              onChange={onReceiptPick}
              style={{ display: 'none' }}
              disabled={loading}
              accept="image/*,application/pdf"
            />

            {receipt && <p className="log-payment-upload-name">{receipt.name}</p>}
          </div>
        </div>

        <div className="log-payment-modal-form-group">
          <label className="log-payment-checkbox-label">
            <input
              type="checkbox"
              checked={includeInExpense}
              onChange={(e) => setIncludeInExpense(e.target.checked)}
              disabled={loading}
            />
            <span>Also add this payment as an Expense entry</span>
          </label>
        </div>

        <div className="log-payment-modal-footer">
          <button
            type="button"
            className="log-payment-modal-btn log-payment-modal-black"
            onClick={handleSubmit}
            disabled={loading || !!amountError}
          >
            {loading ? 'Logging...' : 'Log Payment'}
          </button>

          <button
            type="button"
            className="log-payment-modal-btn log-payment-modal-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogPaymentModal;
